import type { Activity, CheckItem, DocumentItem, Intervention, Lead, LeadActivity, LeadActivityType, LocationDevice, LoginResponse, LookupItem, MobileTab, Paginated, PartReplacementLine, PartSelection, SessionProfile, StockItem, StockMovement, StockReturnRequest, Ticket } from '../types';
import { normalizeChance } from './chance';
import { clientUuid } from './id';

const TOKEN_KEY = 'jwt_token';
export const session = {
  token: () => localStorage.getItem(TOKEN_KEY),
  set: (value: LoginResponse) => { localStorage.setItem(TOKEN_KEY, value.token); localStorage.setItem('user', JSON.stringify(value.user)); },
  setUser: (value: LoginResponse['user']) => localStorage.setItem('user', JSON.stringify(value)),
  clear: () => { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem('user'); },
};

export class ApiError extends Error {
  constructor(readonly status: number, message: string, readonly payload?: unknown) { super(message); }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const token = session.token();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const response = await fetch(`/api${path}`, { ...init, headers, cache: 'no-store' });
  const text = await response.text();
  let payload: unknown = undefined;
  if (text) { try { payload = JSON.parse(text); } catch { payload = text; } }
  if (!response.ok) {
    if (response.status === 401) session.clear();
    if (response.status === 403) window.dispatchEvent(new Event('nova:permissions-stale'));
    const data = payload as { message?: string; error?: string } | undefined;
    throw new ApiError(response.status, data?.message || data?.error || `Cererea a eșuat (${response.status})`, payload);
  }
  return payload as T;
}

async function requestBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const headers = new Headers();
  const token = session.token();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const requestPath = path.startsWith('/api/') ? path : `/api${path}`;
  const response = await fetch(requestPath, { headers, cache: 'no-store', signal });
  if (!response.ok) {
    if (response.status === 401) session.clear();
    let message = `Documentul nu poate fi deschis (${response.status})`;
    try {
      const payload = await response.json() as { message?: string; error?: string };
      message = payload.message || payload.error || message;
    } catch { /* răspuns binar/text fără JSON */ }
    throw new ApiError(response.status, message);
  }
  return response.blob();
}

function query(values: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => value !== undefined && value !== '' && params.set(key, String(value)));
  const result = params.toString();
  return result ? `?${result}` : '';
}

async function workflowRequest<T>(path: string, action: string, payload: unknown): Promise<T> {
  const body = JSON.stringify(payload);
  const revision = (payload as {expectedWorkflowRevision?: number}).expectedWorkflowRevision;
  // Legacy commercial responses have no revision: keep their existing semantics.
  if (!Number.isSafeInteger(revision) || Number(revision) < 0) return request<T>(path, {
    method: 'POST', headers: { 'Idempotency-Key': `fsm:${action}:${clientUuid()}` }, body,
  });
  // Identical retries keep their key across reloads/devices. A new revision or
  // report is a different operation; the server scopes it by tenant and actor.
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify([path, body])));
  const hash = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  return request<T>(path, { method: 'POST', headers: { 'Idempotency-Key': `fsm:${action}:${hash}` }, body });
}

const RESPONSIBLE_ID_FIELD = ['assigned', 'UserId'].join('');
const RESPONSIBLE_IDS_FIELD = ['assigned', 'UserIds'].join('');
const ACTIVITY_TITLES: Record<LeadActivityType, string> = {
  phone_call: 'Apel', meeting: 'Întâlnire', site_visit: 'Vizită', email: 'Email', follow_up: 'Revenire', other: 'Altele',
};
function wireActivity(value: Partial<LeadActivity>): Record<string, unknown> {
  const type: LeadActivityType = value.type || 'other';
  return {
    type, status: value.status, title: ACTIVITY_TITLES[type], notes: value.notes || '', outcome: value.outcome || '',
    startAt: value.startAt, endAt: value.endAt, allDay: value.allDay === true,
    ...(value.responsibleId ? { [RESPONSIBLE_ID_FIELD]: value.responsibleId } : {}),
  };
}
function adaptActivity(value: LeadActivity): LeadActivity {
  return { ...value, responsibleId: String((value as any)?.[RESPONSIBLE_ID_FIELD] || value.responsibleId || '') };
}
function wireCheck(value: Partial<CheckItem>): Record<string, unknown> {
  return {
    addressId: value.addressId, equipmentId: value.equipmentId, observations: value.observations || '', performedAt: value.performedAt,
    lat: value.lat, lon: value.lon, accuracy: value.accuracy, capturedAt: value.capturedAt,
  };
}
function wireLead(value: Partial<Lead>): Record<string, unknown> {
  return {
    nume: value.nume || '', firma: value.firma || '', cui: value.cui || '', telefon: value.telefon || '',
    email: value.email || '', localitate: value.localitate || '', strada: value.adresa || '', status: value.status || '', chance: normalizeChance(value.chance), sursa: value.sursa || '',
    observatii: value.observations || '', ...(value.responsibleId ? { [RESPONSIBLE_ID_FIELD]: value.responsibleId } : {}),
    ...(value.lat != null && value.lon != null && Number.isFinite(Number(value.lat)) && Number.isFinite(Number(value.lon)) ? {
      lat: Number(value.lat), lon: Number(value.lon), accuracy: Number.isFinite(Number(value.accuracy)) ? Number(value.accuracy) : null,
      capturedAt: value.capturedAt || new Date().toISOString(),
    } : {}),
  };
}
function adaptLead(value: Lead): Lead {
  const raw = value as Lead & { strada?: string; observatii?: string; latitude?: number | null; longitude?: number | null; [RESPONSIBLE_ID_FIELD]?: string };
  const lat = value.lat ?? raw.latitude;
  const lon = value.lon ?? raw.longitude;
  return {
    ...value,
    adresa: value.adresa || raw.strada || '',
    observations: value.observations || raw.observatii || '',
    responsibleId: String(raw[RESPONSIBLE_ID_FIELD] || value.responsibleId || ''),
    chance: normalizeChance(value.chance),
    lat: lat == null ? null : Number(lat), lon: lon == null ? null : Number(lon),
  };
}
function adaptLeadPayload(payload: any): any {
  if (Array.isArray(payload)) return payload.map(adaptLead);
  if (Array.isArray(payload?.items)) return { ...payload, items: payload.items.map(adaptLead) };
  if (Array.isArray(payload?.rows)) return { ...payload, rows: payload.rows.map(adaptLead) };
  return adaptLead(payload?.lead || payload?.item || payload?.row || payload);
}
function adaptCheck(value: CheckItem): CheckItem {
  const raw = value as CheckItem & {
    addressSnapshot?: Record<string, unknown>;
    equipmentSnapshot?: LocationDevice;
    ownerSnapshot?: { name?: string; fullName?: string; email?: string };
  };
  const address = value.address || raw.addressSnapshot || {};
  const equipment = value.equipment || raw.equipmentSnapshot;
  const assignedIds = (raw as Record<string, unknown>)[RESPONSIBLE_IDS_FIELD];
  return {
    ...value,
    address,
    addressName: value.addressName || String(address.name || address.denumire || address.address || address.adresa || ''),
    equipment,
    equipmentId: String(value.equipmentId || equipment?.equipmentId || equipment?.deviceId || equipment?._id || equipment?.id || ''),
    ownerName: value.ownerName || raw.ownerSnapshot?.name || raw.ownerSnapshot?.fullName || raw.ownerSnapshot?.email || '',
    responsibleIds: Array.isArray(assignedIds) ? assignedIds.map(String) : value.responsibleIds || [],
  };
}
function adaptCheckPayload(payload: any): any {
  if (Array.isArray(payload)) return payload.map(adaptCheck);
  if (Array.isArray(payload?.items)) return { ...payload, items: payload.items.map(adaptCheck) };
  if (Array.isArray(payload?.rows)) return { ...payload, rows: payload.rows.map(adaptCheck) };
  return adaptCheck(payload?.check || payload?.item || payload?.row || payload);
}

export function ticketRunId(ticket: Partial<Ticket>, fallback = ''): string {
  return String(
    ticket.runId
    || ticket.intervention?.runId
    || ticket.interventionReport?.runId
    || fallback
    || ''
  ).trim();
}

export const api = {
  login: (username: string, password: string) => request<LoginResponse>('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),
  logout: () => request<{ success: boolean }>('/auth/logout', { method: 'POST' }),
  me: () => request<SessionProfile>('/auth/me'),
  pushConfig: () => request<{ publicKey: string }>('/push/config'),
  pushSubscribe: (subscription: PushSubscriptionJSON) => request<{ ok: boolean }>('/push/subscribe', { method: 'POST', body: JSON.stringify({ endpoint: subscription.endpoint, keys: subscription.keys, userAgent: navigator.userAgent }) }),
  pushUnsubscribe: (endpoint: string) => request<{ ok: boolean }>('/push/subscribe', { method: 'DELETE', body: JSON.stringify({ endpoint }) }),
  // Arrival in the technician's queue is the assignment date, not the last edit.
  tickets: (mobileTab: MobileTab | undefined, page = 1, limit = 20, _fresh = false, signal?: AbortSignal) => request<import('../types').TicketPage>(`/tickets${query({ mobileTab, page, limit })}`, { signal }),
  ticket: (id: string, runId = '') => request<Ticket>(`/tickets/${encodeURIComponent(id)}${query({ runId })}`),
  ticketHistory: (id: string, page = 1, limit = 10) => request<Paginated<Ticket>>(`/tickets/${encodeURIComponent(id)}/history${query({ page, limit })}`),
  seen: (id: string) => request(`/tickets/${encodeURIComponent(id)}/seen`, { method: 'POST' }),
  intervention: (id: string, runId = '') => request<{ intervention?: Intervention } & Partial<Ticket>>(`/tickets/${encodeURIComponent(id)}/intervention${query({ runId })}`),
  action: (id: string, action: 'start' | 'pause' | 'resume', ticket: Ticket) => workflowRequest<Partial<Ticket>>(`/tickets/${encodeURIComponent(id)}/intervention/${action}${action === 'start' ? '' : query({ runId: ticketRunId(ticket) })}`, action, { expectedWorkflowRevision: ticket.workflowRevision }),
  finalize: (id: string, ticket: Ticket, activityIds: string[], observations: string, clientSignature = '', fallbackRunId = '', clientLastName = '', clientFirstName = '') => workflowRequest<Partial<Ticket>>(`/tickets/${encodeURIComponent(id)}/intervention/finalize${query({ runId: ticketRunId(ticket, fallbackRunId) })}`, 'finalize', { expectedWorkflowRevision: ticket.workflowRevision, report: { activityIds, observations, clientLastName, clientFirstName, clientSignature } }),
  activities: () => request<Activity[]>('/ticket-activities'),
  ticketActivities: (id: string) => request<Activity[]>(`/tickets/${encodeURIComponent(id)}/activities`),
  documents: (id: string, runId = '', signal?: AbortSignal) => request<DocumentItem[]>(`/tickets/${encodeURIComponent(id)}/documents${query({ runId })}`, { signal }),
  upload: (id: string, files: File[], runId = '') => { const body = new FormData(); files.forEach((file) => body.append('files', file)); return request<import('../types').DocumentUploadResult>(`/tickets/${encodeURIComponent(id)}/documents${query({ runId })}`, { method: 'POST', body }); },
  documentBlob: (url: string, signal?: AbortSignal) => requestBlob(url, signal),
  stock: () => request<StockItem[]>('/stock/operator/me'),
  partCatalog: () => request<StockItem[]>('/stock/catalog'),
  defectStock: () => request<StockItem[]>('/stock/defects/me'),
  defectReturn: (parts: PartSelection[], notes = '') => request('/stock/defects/returns', { method: 'POST', body: JSON.stringify({ parts, notes }) }),
  stockReturns: () => request<StockReturnRequest[]>('/stock/returns/me'),
  stockReturnRequest: (kind: 'good' | 'defective', parts: PartSelection[], notes: string, operationId: string) => request('/stock/returns', {
    method: 'POST',
    headers: { 'Idempotency-Key': operationId },
    body: JSON.stringify({ kind, parts, notes }),
  }),
  cancelStockReturn: (id: string, reason = '') => request(`/stock/returns/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),
  centralStock: () => request<StockItem[]>('/stock/central'),
  stockMovements: (from: string, to: string, page = 1, limit = 20) => request<Paginated<StockMovement>>(`/stock/movements${query({ from, to, page, limit, sortField: 'createdAt', sortDir: 'desc' })}`),
  stockTransfer: (parts: PartSelection[]) => request('/stock/transfer', { method: 'POST', body: JSON.stringify({ parts }) }),
  consume: (ticket: Ticket, parts: Array<{ partId: string; partCode: string; qty: number }>, fallbackRunId = '') => {
    const runId = ticketRunId(ticket, fallbackRunId);
    return request(`/tickets/${encodeURIComponent(ticket.ticketId)}/parts/consume${query({ runId })}`, {
      method: 'POST',
      body: JSON.stringify({ parts, clientId: ticket.clientId, runId, workRunId: runId }),
    });
  },
  replacements: (ticket: Ticket, replacements: PartReplacementLine[], fallbackRunId = '') => {
    const runId = ticketRunId(ticket, fallbackRunId);
    return request(`/tickets/${encodeURIComponent(ticket.ticketId)}/parts/replacements${query({ runId })}`, {
      method: 'POST',
      body: JSON.stringify({
        runId,
        workRunId: runId,
        expectedWorkflowRevision: ticket.workflowRevision,
        replacements,
      }),
    });
  },
  leads: (values: { page?: number; limit?: number; search?: string; chanceMin?: number; chanceMax?: number; source?: string; assignedUser?: string }) => request<any>(`/leads${query(values)}`).then(adaptLeadPayload),
  lead: (id: string) => request<Lead>(`/leads/${encodeURIComponent(id)}`).then(adaptLeadPayload),
  createLead: (value: Partial<Lead>) => request<Lead>('/leads', { method: 'POST', body: JSON.stringify(wireLead(value)) }).then(adaptLeadPayload),
  updateLead: (id: string, value: Partial<Lead>) => request<Lead>(`/leads/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(wireLead(value)) }).then(adaptLeadPayload),
  deleteLead: (id: string) => request(`/leads/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  leadDocuments: (id: string) => request<DocumentItem[]>(`/leads/${encodeURIComponent(id)}/documents`),
  uploadLeadDocument: (id: string, file: File) => { const body = new FormData(); body.append('files', file); return request(`/leads/${encodeURIComponent(id)}/documents`, { method: 'POST', body }); },
  deleteLeadDocument: (leadId: string, documentId: string) => request(`/leads/${encodeURIComponent(leadId)}/documents/${encodeURIComponent(documentId)}`, { method: 'DELETE' }),
  leadActivities: (id: string) => request<any>(`/leads/${encodeURIComponent(id)}/activities`).then((payload) => Array.isArray(payload) ? payload.map(adaptActivity) : Array.isArray(payload?.items) ? { ...payload, items: payload.items.map(adaptActivity) } : Array.isArray(payload?.rows) ? { ...payload, rows: payload.rows.map(adaptActivity) } : payload),
  createLeadActivity: (id: string, value: Partial<LeadActivity>) => request<LeadActivity>(`/leads/${encodeURIComponent(id)}/activities`, { method: 'POST', body: JSON.stringify(wireActivity(value)) }).then(adaptActivity),
  updateLeadActivity: (leadId: string, activityId: string, value: Partial<LeadActivity>) => request<LeadActivity>(`/leads/${encodeURIComponent(leadId)}/activities/${encodeURIComponent(activityId)}`, { method: 'PATCH', body: JSON.stringify(wireActivity(value)) }).then(adaptActivity),
  deleteLeadActivity: (leadId: string, activityId: string) => request(`/leads/${encodeURIComponent(leadId)}/activities/${encodeURIComponent(activityId)}`, { method: 'DELETE' }),
  checks: (values: { page?: number; limit?: number; addressId?: string; responsibleId?: string; owner?: string; from?: string; to?: string }) => { const { responsibleId, ...rest } = values; return request<any>(`/checks${query({ ...rest, ...(responsibleId ? { [RESPONSIBLE_ID_FIELD]: responsibleId } : {}) })}`).then(adaptCheckPayload); },
  check: (id: string) => request<CheckItem>(`/checks/${encodeURIComponent(id)}`).then(adaptCheckPayload),
  createCheck: (value: Partial<CheckItem>) => request<CheckItem & { checkId?: string }>('/checks', { method: 'POST', body: JSON.stringify(wireCheck(value)) }).then(adaptCheckPayload),
  updateCheck: (id: string, value: Partial<CheckItem>) => request<CheckItem>(`/checks/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(wireCheck(value)) }).then(adaptCheckPayload),
  deleteCheck: (id: string) => request(`/checks/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  checkDocuments: (id: string) => request<DocumentItem[]>(`/checks/${encodeURIComponent(id)}/documents`),
  uploadCheckDocument: (id: string, file: Blob, fileName = 'foto.jpg') => request(`/checks/${encodeURIComponent(id)}/documents/raw`, {
    method: 'POST',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'X-File-Name': encodeURIComponent(fileName || 'foto.jpg'),
      'X-File-Size': String(file.size),
    },
    body: file,
  }),
  deleteCheckDocument: (checkId: string, documentId: string) => request(`/checks/${encodeURIComponent(checkId)}/documents/${encodeURIComponent(documentId)}`, { method: 'DELETE' }),
  locations: (search = '') => request<LookupItem[]>(`/locations${query({ search, limit: 50 })}`),
  equipment: (locationId: string, search = '') => request<LocationDevice[]>(`/equipment${query({ locationId, search, page: 1, limit: 50 })}`),
  tenantUsers: (search = '') => request<any>('/lookups/query', { method: 'POST', body: JSON.stringify({ module: 'tenant_users', search, limit: 30, fields: ['email', 'fullName'] }) }),
};
